import type { AdminOverview } from '@steado/admin-contract/overview';

import { Panel } from '@/components/panel/panels';
import { TONES } from '@/components/table/tone';
import { formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The same probes a load balancer calls, plus what the queues are holding.
 *
 * One row per thing that can be down, each a dot and a reading. The footer
 * carries the two caveats that would otherwise be read off wrongly: how long
 * the aggregates took to build (design spec §12 deferred optimising them, and
 * a number is how anyone would know that changed), and that an empty queue
 * list means Redis was unreachable rather than that nothing has failed.
 */
export function HealthPanel({ overview }: { readonly overview: AdminOverview }) {
  const { health } = overview;

  const rows: readonly { key: string; name: string; dot: string; value: string; valueClass: string }[] = [
    ...health.dependencies.map((dependency) => ({
      key: `dep:${dependency.name}`,
      name: dependency.name,
      dot: dependency.status === 'up' ? 'bg-ok-fg' : 'bg-err-fg',
      value: dependency.status === 'up' ? `${String(dependency.latencyMs ?? 0)}ms` : 'down',
      valueClass: dependency.status === 'up' ? 'text-muted-foreground' : 'text-err-fg',
    })),
    ...health.queues.map((queue) => ({
      key: `queue:${queue.name}`,
      name: queue.name,
      dot: queue.isPaused ? 'bg-warn-fg' : queue.failed > 0 ? 'bg-err-fg' : 'bg-ok-fg',
      value: `${String(queue.failed)} failed · ${String(queue.waiting)} waiting`,
      valueClass: queue.failed > 0 ? 'text-err-fg' : 'text-muted-foreground',
    })),
    {
      key: 'uptime',
      name: `Up ${formatDuration(health.uptimeSeconds * 1000) ?? 'just now'}`,
      dot: 'bg-mute-fg',
      value: health.version,
      valueClass: 'text-muted-foreground',
    },
  ];

  return (
    <Panel
      title="Dependencies and queues"
      action={
        <span
          className={cn(
            'inline-flex min-h-5 items-center rounded-[10px] px-2 text-xs font-semibold',
            TONES[health.status === 'ok' ? 'ok' : 'err'],
          )}
        >
          {health.status}
        </span>
      }
      bleed
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          Aggregates took <span className="font-mono">{overview.queryMs}ms</span> to build.{' '}
          {health.queues.length === 0
            ? 'No queue row is listed, which means Redis was unreachable, not that nothing failed.'
            : 'An empty queue list would mean Redis was unreachable, not that nothing failed.'}
        </p>
      }
    >
      <ul>
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2.5 border-t border-border px-[18px] py-2">
            <span aria-hidden="true" className={cn('inline-block size-1.5 shrink-0 rounded-full', row.dot)} />
            <span className="truncate text-[13px] text-foreground">{row.name}</span>
            <span className={cn('ml-auto shrink-0 font-mono text-[12.5px]', row.valueClass)}>
              {row.value}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
